/**
 * debugState.ts — Global debug flags read by game systems each frame.
 * Toggle the panel with the ` (backtick) key in-game.
 */

// ─── Live toggles ─────────────────────────────────────────────────────────────
export let debugGodMode          = false;   // Player takes no damage
export let debugNavOverlay       = false;   // Draw rooms, walls, gaps, triangles
export let debugShowWaypoints    = false;   // Draw enemy → waypoint arrows
export let debugFlowField        = false;   // Draw flow field arrows + heat map
export let debugFlowFieldMode: 'arrows' | 'heat' | 'cost' = 'arrows'; // Which flow viz
export let debugDynamicLighting  = false;  // BETA 20.95 — OFF by default. Composites a dark "night" layer over the world with a soft glow around the player. Players are unaffected until this is flipped on.
export let debugWeaponSockets    = false;  // BETA 22.69 — red dot = muzzle socket, blue = pivot, line = aim. Verifies projectiles leave the drawn barrel.
export let debugEnemyHpMult      = 1.0;    // Multiply all enemy max HP on spawn
export let debugEnemyDamageMult  = 1.0;    // BETA 20.91 — multiply ALL enemy→player damage (contact/slam/throw). The live "how hard do they hit" difficulty knob.
export let debugPlayerSpeedMult  = 1.0;    // Applied to player.speed every frame
export let debugPlayerDamageMult = 1.0;    // Multiplied into every weapon hit
// BETA 13.62: persisted across page reloads. User direction "speed 1× should stay
// that way for every run" — we were resetting to module-default 1.0 on each reload
// but the live in-memory value would drift after manual changes. Now restored from
// localStorage so the bot's overnight loop is deterministic.
export let debugTimeScale        = (() => {
  try {
    const raw = localStorage.getItem('goopzone_time_scale');
    const v = raw != null ? parseFloat(raw) : 1.0;
    return Number.isFinite(v) && v > 0 ? v : 1.0;
  } catch { return 1.0; }
})();

export function setDebugGodMode         (v: boolean) { debugGodMode          = v; }
export function setDebugNavOverlay      (v: boolean) { debugNavOverlay       = v; }
export function setDebugShowWaypoints   (v: boolean) { debugShowWaypoints    = v; }
export function setDebugFlowField       (v: boolean) { debugFlowField        = v; }
export function setDebugFlowFieldMode   (v: 'arrows' | 'heat' | 'cost') { debugFlowFieldMode = v; }
export function setDebugDynamicLighting (v: boolean) { debugDynamicLighting  = v; }
export function setDebugWeaponSockets   (v: boolean) { debugWeaponSockets    = v; }
export function setDebugEnemyHpMult     (v: number)  { debugEnemyHpMult      = v; }
export function setDebugEnemyDamageMult (v: number)  { debugEnemyDamageMult  = v; }
export function setDebugPlayerSpeedMult (v: number)  { debugPlayerSpeedMult  = v; }
export function setDebugPlayerDamageMult(v: number)  { debugPlayerDamageMult = v; }
export function setDebugTimeScale       (v: number)  {
  debugTimeScale        = v;
  try { localStorage.setItem('goopzone_time_scale', String(v)); } catch { /* private mode */ }
}

// BETA 17.96 — Master VFX toggle. Per Dylan: "can you give us a vfx toggle on
// and off?" When false, the renderer skips: particle systems, item-effect
// bursts (rocket explosions, etc.), screen overlays (Last Stand red flash,
// low-HP vignette, hit-flash tints). Game simulation runs normally — only
// the visual decoration is suppressed. Persists across reloads via
// localStorage so the toggle stays sticky between dev sessions.
export let vfxEnabled = (() => {
  try {
    const raw = localStorage.getItem('goopzone_vfx_enabled');
    if (raw == null) return true;
    return raw === '1' || raw === 'true';
  } catch { return true; }
})();
export function isVfxEnabled(): boolean { return vfxEnabled; }
export function setVfxEnabled(v: boolean): void {
  vfxEnabled = v;
  try { localStorage.setItem('goopzone_vfx_enabled', v ? '1' : '0'); } catch { /* private mode */ }
}

// ─── Session data logger ──────────────────────────────────────────────────────
export interface DebugLogEntry {
  time:  string;
  event: string;
  value: string;
}

const _log: DebugLogEntry[] = [];

export function logDebug(event: string, value: string | number): void {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  _log.push({ time: `${hh}:${mm}:${ss}`, event, value: String(value) });
}

export function getDebugLog(): DebugLogEntry[] { return _log; }

export function downloadDebugCsv(): void {
  const rows = ['Time,Event,Value', ..._log.map(e => `${e.time},${e.event},${e.value}`)];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `goopzone-debug-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
